import { useEffect, useState } from "react";

type ShareSheetProps = {
  open: boolean;
  title: string;
  onClose: () => void;
};

const ShareSheet = ({ open, title, onClose }: ShareSheetProps) => {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setCopyFailed(false);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!open) return null;

  const url = window.location.href;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopyFailed(true);
    }
  };

  const shareLinks = [
    {
      label: "微博",
      icon: "ri-weibo-fill",
      href: `https://service.weibo.com/share/share.php?url=${encodeURIComponent(url)}&title=${encodeURIComponent(`推荐一部经典影视：${title}`)}`,
    },
    {
      label: "豆瓣",
      icon: "ri-book-2-line",
      href: "https://www.douban.com/",
    },
    {
      label: "X",
      icon: "ri-twitter-x-fill",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`推荐一部经典影视：${title}`)}&url=${encodeURIComponent(url)}`,
    },
    {
      label: "邮件",
      icon: "ri-mail-line",
      href: `mailto:?subject=${encodeURIComponent(`影视推荐：${title}`)}&body=${encodeURIComponent(`分享一个影视资料页：${url}`)}`,
    },
  ];

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center md:items-center">
      <button type="button" aria-label="关闭分享" onClick={onClose} className="absolute inset-0 bg-foreground-950/50" />
      <div className="relative w-full animate-pop-in rounded-t-2xl bg-background-50 p-5 md:max-w-md md:rounded-lg">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold text-foreground-950">分享这部作品</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-700"
          >
            <i className="ri-close-line" />
          </button>
        </div>
        <p className="mt-1 truncate text-xs text-foreground-500">{title}</p>

        <div className="mt-4 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5">
          <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
            <i className="ri-link" />
          </span>
          <input
            readOnly
            value={url}
            aria-label="页面链接"
            className="min-w-0 flex-1 bg-transparent text-xs text-foreground-700 outline-none"
          />
          <button
            type="button"
            onClick={copy}
            className="shrink-0 cursor-pointer rounded bg-primary-500 px-2.5 py-1.5 text-xs font-medium text-background-50 hover:bg-primary-600"
          >
            {copied ? "已复制" : "复制"}
          </button>
        </div>
        {copied ? (
          <p className="mt-2 flex items-center gap-1 text-xs text-primary-600">
            <i className="ri-check-line" /> 链接已复制到剪贴板
          </p>
        ) : null}
        {copyFailed ? (
          <p className="mt-2 text-xs text-accent-700">
            浏览器未授权剪贴板访问，请手动选中上方链接复制。
          </p>
        ) : null}

        <div className="mt-5 grid grid-cols-4 gap-2">
          {shareLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noreferrer noopener"
              className="flex cursor-pointer flex-col items-center gap-1.5 rounded-md bg-background-100 py-3 text-foreground-700 transition-colors hover:bg-secondary-100"
            >
              <span className="flex h-6 w-6 items-center justify-center text-lg">
                <i className={item.icon} />
              </span>
              <span className="text-xs">{item.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShareSheet;